import { BarDatum, BarLayer, ResponsiveBar } from '@nivo/bar';
import React, { memo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import NoAvailable from './NoAvailable';
import { Formatter } from '../utils/Formatter';
import { Currency } from '../domain/transaction';
import ChartPoint from './ChartPoint';
import ChartLine from './ChartLine';

export type BarChartVariant = 'bars' | 'points-line';

interface Data {
  data: BarDatum[],
  showAxisBottomLegend?: boolean,
  variant?: BarChartVariant
}

// Azul (info.800) y el naranja del portal (button-primary.500 / primary.800), alternados
// por barra.
const BAR_CHART_COLORS = ['#3665B2', '#F14A1D'];

// Ancho mínimo por barra: con muchas barras (Mes ~20 días, Año hasta 12 meses) el
// contenedor completo se ve chico/apretado si se fuerza todo a caber en el 100% del
// ancho disponible. En vez de eso, la gráfica crece más allá del contenedor y este se
// vuelve scrolleable horizontalmente (ver overflowX en el Box de más abajo).
const MIN_BAR_WIDTH_PX = 60;

// En la variante de puntos y líneas todas las marcas van en el naranja del portal, una
// línea une los puntos (dibujada antes de la capa `bars` para quedar detrás de ellos) y
// el eje X se muestra sin rotar, saltando etiquetas para que quepan sin scroll
// horizontal. Con 16 como máximo, un mes de 31 días muestra uno de cada dos días
// empezando en el primero.
const POINT_COLOR = '#F14A1D';
const MAX_POINT_AXIS_TICKS = 16;
const POINTS_LINE_LAYERS: BarLayer<BarDatum>[] = ['grid', 'axes', ChartLine, 'bars', 'markers', 'legends', 'annotations'];

const getColor = (bar:any) => {
  return BAR_CHART_COLORS[bar.index % BAR_CHART_COLORS.length];
};

const getPointTickValues = (data: BarDatum[], indexBy: string) => {
  const step = Math.ceil(data.length / MAX_POINT_AXIS_TICKS);
  return data.filter((_, index) => index % step === 0).map((datum) => datum[indexBy]);
};

const formatCurrency = (value: number) => Formatter.toCurrency(value, Currency.mex);

const INDEX_BY = 'timePeriod';

const BarChart = ({data, showAxisBottomLegend = true, variant = 'bars'}:Data) => {
  if (data.length === 0) {
    return <NoAvailable />
  }
  const isPointsLine = variant === 'points-line';
  return (
    <Box width="100%" height="30rem" overflowX="auto">
      <Box minWidth="100%" width={isPointsLine ? '100%' : `${data.length * MIN_BAR_WIDTH_PX}px`} height="100%">
        <ResponsiveBar
          data={data}
          keys={["transactions"]}
          indexBy={INDEX_BY}
          margin={{ top: 10, right: 0, bottom: 50, left: 80 }}
          padding={0.5}

          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          borderRadius={8}
          groupMode="grouped"
          colors={isPointsLine ? POINT_COLOR : getColor}
          barComponent={isPointsLine ? ChartPoint : undefined}
          layers={isPointsLine ? POINTS_LINE_LAYERS : undefined}
          axisBottom={ showAxisBottomLegend ? {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: isPointsLine ? 0 : -35,
            tickValues: isPointsLine ? getPointTickValues(data, INDEX_BY) : undefined,
            legendPosition: "middle",
            legendOffset: 50,
            //format: (v) => v.substring(0, 3),
          } : null}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legendPosition: "middle",
            legendOffset: -105,
            format: (value) => formatCurrency(Number(value)),
          }}
          valueFormat={(value) => `` }
          tooltip={point => {
            return <Box p={1} borderRadius={5} bg="button-primary.500" >
              <Text color={'white'}> {point.data.timePeriod} - {formatCurrency(Number(point.value))}
              </Text>
            </Box>;
          }}
          labelSkipWidth={20}
          labelSkipHeight={50}
          role="application"
          barAriaLabel={function (e) {
            return e.id + ": " + e.formattedValue + " in month: " + e.indexValue;
          }}
        />
      </Box>
    </Box>
  )
}

export default memo(BarChart)
